import type { Client } from '../../types';

export function normalizeClientPayload(raw: unknown): Client | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  return {
    ...(r as unknown as Client),
    id: String(r.id),
    plans: (r.clientPlans as Client['plans']) || (r.plans as Client['plans']) || [],
    familyMembers: (r.familyMembers as Client['familyMembers']) || []
  };
}

export function getRelationshipLabel(relationship: string): string {
  const labels: Record<string, string> = {
    esposo: 'Esposo(a)',
    hijo: 'Hijo(a)',
    padre: 'Padre',
    madre: 'Madre',
    hermano: 'Hermano(a)',
    abuelo: 'Abuelo(a)',
    nieto: 'Nieto(a)',
    tio: 'Tío(a)',
    primo: 'Primo(a)',
    suegro: 'Suegro(a)',
    yerno: 'Yerno/Nuera',
    cuñado: 'Cuñado(a)',
    otro: 'Otro'
  };
  return labels[relationship] || relationship;
}

export function profileInitials(name: string, lastname: string): string {
  const a = (name || '').trim().charAt(0);
  const b = (lastname || '').trim().charAt(0);
  return `${a}${b}`.toUpperCase() || '—';
}

export function profileAvatarClass(seed: string): string {
  const tones = [
    'bg-slate-200 text-slate-900 ring-slate-300/80',
    'bg-indigo-100 text-indigo-900 ring-indigo-200/80',
    'bg-teal-100 text-teal-900 ring-teal-200/80',
    'bg-amber-100 text-amber-900 ring-amber-200/80',
    'bg-rose-100 text-rose-900 ring-rose-200/80'
  ];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = seed.charCodeAt(i) + ((h << 5) - h);
  return tones[Math.abs(h) % tones.length];
}

type PaymentLike = { amount?: number; paymentDate?: string };
type InvoiceLike = { status?: string };

export interface ClientFinancialSummary {
  pendingInvoices: number;
  overdueInvoices: number;
  totalPaid: number;
  lastPaymentDate: string | null;
  paymentCount: number;
}

export function computeFinancialSummary(
  client: Client & { payments?: PaymentLike[]; clientPlans?: { invoices?: InvoiceLike[] }[] }
): ClientFinancialSummary {
  const plans = (client as { clientPlans?: typeof client.plans }).clientPlans || client.plans || [];
  let pendingInvoices = 0;
  let overdueInvoices = 0;
  for (const p of plans as { invoices?: InvoiceLike[] }[]) {
    const invs = p.invoices || [];
    for (const inv of invs) {
      if (inv.status === 'PENDING' || inv.status === 'OVERDUE') pendingInvoices++;
      if (inv.status === 'OVERDUE') overdueInvoices++;
    }
  }

  const payments = client.payments || [];
  let totalPaid = 0;
  let last: Date | null = null;
  for (const pay of payments) {
    totalPaid += Number(pay.amount) || 0;
    if (pay.paymentDate) {
      const d = new Date(pay.paymentDate);
      if (!last || d > last) last = d;
    }
  }

  return {
    pendingInvoices,
    overdueInvoices,
    totalPaid,
    lastPaymentDate: last ? last.toISOString() : null,
    paymentCount: payments.length
  };
}

export type FamilyGroupKey = 'baseFamily' | 'hijos' | 'hermanos' | 'suegros' | 'otros';

export function groupFamilyMembers(client: Client | null) {
  const baseFamily: NonNullable<Client['familyMembers']> = [];
  const hijos: NonNullable<Client['familyMembers']> = [];
  const hermanos: NonNullable<Client['familyMembers']> = [];
  const suegros: NonNullable<Client['familyMembers']> = [];
  const otros: NonNullable<Client['familyMembers']> = [];
  if (!client?.familyMembers) {
    return { baseFamily, hijos, hermanos, suegros, otros };
  }

  for (const fm of client.familyMembers) {
    const relationship = fm.relationship.toLowerCase();
    if (relationship === 'padre' || relationship === 'madre' || relationship === 'esposo' || relationship === 'esposa') {
      baseFamily.push(fm);
    } else if (relationship === 'hijo' || relationship === 'hija') {
      hijos.push(fm);
    } else if (relationship === 'hermano' || relationship === 'hermana') {
      hermanos.push(fm);
    } else if (relationship === 'suegro' || relationship === 'suegra') {
      suegros.push(fm);
    } else {
      otros.push(fm);
    }
  }
  return { baseFamily, hijos, hermanos, suegros, otros };
}
